import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { claimExtraCharges } from '../../services/api/implementation/impl';
import {
  INVOICE_SOURCE_CREDIT_NOTE,
  INVOICE_SOURCE_DEBIT_NOTE
} from '../../utils/invoiceDealAmounts';

const NOTE_TYPES = [
  { value: INVOICE_SOURCE_DEBIT_NOTE, label: 'Debit Note' },
  { value: INVOICE_SOURCE_CREDIT_NOTE, label: 'Credit Note' }
];

const DebitCreditNoteForm = ({ isOpen, onClose, dealId, onSuccess }) => {
  const [noteType, setNoteType] = useState(INVOICE_SOURCE_DEBIT_NOTE);
  const [amount, setAmount] = useState('');
  const [gstPercentage, setGstPercentage] = useState('0');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setNoteType(INVOICE_SOURCE_DEBIT_NOTE);
    setAmount('');
    setGstPercentage('0');
    setInvoiceNumber('');
    setIssueDate('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!invoiceNumber?.trim()) {
      toast.error('Please enter note number');
      return;
    }
    if (!issueDate) {
      toast.error('Please select issue date');
      return;
    }

    const payload = {
      deal_id: dealId,
      source: noteType,
      amount: parsedAmount,
      gst_percentage: Number(gstPercentage || 0),
      invoiceNumber: invoiceNumber.trim(),
      issueDate,
      type: 'Builder'
    };

    setSubmitting(true);
    try {
      const created = await claimExtraCharges(payload);
      if (!created?.id) {
        toast.error(`${noteType} was not created`);
        return;
      }
      toast.success(`${noteType} added successfully`);
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || `Error adding ${noteType}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isDirty={true}
      confirmOnClose={false}
      name={`Add ${noteType}`}
      onClose={handleClose}
      size="md">
      <div className="space-y-4 p-1">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Note type</label>
          <div className="flex gap-2">
            {NOTE_TYPES.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setNoteType(option.value)}
                className={`flex-1 px-3 py-2 rounded-md text-sm border ${
                  noteType === option.value
                    ? 'bg-[#346C75] text-white border-[#346C75]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}>
                {option.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Debit notes increase Total and Due. Credit notes reduce Total and Due.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="Enter amount"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GST %</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={gstPercentage}
            onChange={(e) => setGstPercentage(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="e.g. DN-001 / CN-001"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Issue date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" displayType="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : `Add ${noteType}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DebitCreditNoteForm;
